/**
 * Detailed Publish Impact store.
 *
 * Owns the completed-publish artifact reads that power the
 * "View detailed publish results" dialog opened from `PublishFlow.vue`.
 *
 * This store is separate from `useDetailedImpactStore` because the two
 * surfaces answer different questions:
 *
 * - Preview impact is simulated before publish and is keyed by `simulationId`.
 *   Its copy is future-tense: what will happen if the user publishes.
 * - Publish impact is based on a completed run and is keyed by
 *   `(jazelId, correlationId)`. Its copy is past-tense: what happened in
 *   the publish run.
 *
 * The parent Publish Flow view owns manifest loading. This store receives a
 * manifest snapshot, then reads decision artifacts advertised by
 * `manifest.stageDecisions`.
 *
 * Decision browsing is stage-keyed. Each stage has independent pages, status,
 * and cursor state so switching tabs never clears another stage's results, and
 * VIN lookup never wipes the stage browser.
 *
 * Browser stages follow true publish-pipeline order:
 *
 *   preProcessor -> feedFieldRules
 *   -> fieldMapping -> groups -> listingRules
 *
 * Stage tabs are shown only when real decision pointers exist in
 * `manifest.stageDecisions`. High-level manifest summaries never create a tab
 * on their own.
 *
 * Feed-Field Rules are shown as their own observable decision category, but
 * they are captured during Field Mapping because they transform mapped vehicle
 * data and depend on the field-mapping input/output path.
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  tryGetPublishFlowStageDecisions,
  tryGetPublishFlowVinTrail,
  type PublishFlowSupportedDecisionStage,
  type PublishStageDecisionsPage,
  type PublishVinTrail,
} from '@/services/publishFlowDecisionService'
import {
  tryGetBlockedDecisions,
  type BlockedDecisionRecord,
  type BlockedDecisionsPage,
} from '@/services/publishFlowBlockedDecisionsService'
import type { PublishDecisionRecord } from '@/services/publishPreviewArtifactsService'
import type {
  PublishFlowObservabilityManifest,
} from '@/services/publishFlowObservabilityService'

/**
 * VIN-trail status machine.
 *
 * Distinct from the Preview store in two important ways:
 *   - `'notInRun'` — Adjustment #1: the run completed AND we know
 *     this VIN was not represented in the listingRules decisions for
 *     the run. The dialog renders a dedicated "no results" alert and
 *     SUPPRESSES the per-stage list. This is intentionally separate
 *     from `'unavailable'` (which means "we couldn't load the data").
 *   - `'notFound'` is reachable only if the manifest itself
 *     disappears between the parent's gating and this lookup. The
 *     dialog collapses it into the same copy as `'unavailable'` for
 *     the user, but the store keeps the distinction so on-call can
 *     trace the cause via test fixtures and structured logs.
 */
export type VinTrailStatus =
  | 'idle'
  | 'invalid'
  | 'loading'
  | 'ok'
  | 'notInRun'
  | 'notFound'
  | 'unavailable'

/**
 * Browser status machine for the paginated stage-decisions view.
 *
 * Note that `'notFound'` is intentionally NOT in this set: the dialog
 * mounts only when the manifest already advertises a decision pointer
 * for at least one stage (`detailedImpactReady` in `PublishFlow.vue`),
 * so a 404 here would indicate the manifest disappeared between the
 * gate and the fetch — the same race the VIN-trail surface handles. We
 * collapse it into `'unavailable'` so the dialog renders a single
 * friendly message rather than a separate notFound branch the dealer
 * couldn't act on.
 */
export type BrowserStatus = 'idle' | 'loading' | 'ok' | 'unavailable'

/**
 * Dialog content mode. The two non-`'default'` values are self-contained
 * "panel modes" that own their own browse + search and suppress the stage
 * browser / cross-stage VIN lookup. Neither is a new visible stage.
 */
export type DetailedPublishImpactDialogMode =
  | 'default'
  | 'passThrough'

/** Panel modes own their own fetches; the stage browser stays inert. */
const PANEL_DIALOG_MODES: ReadonlySet<DetailedPublishImpactDialogMode> = new Set([
  'passThrough',
])

export type BrowserSlice = {
  pages: PublishStageDecisionsPage[]
  status: BrowserStatus
  cursor: string | undefined
}

/**
 * Phase 3 — blocked-decisions drill-down slice. A single run-wide slice (not
 * per-stage) backing the listingRules "Blocked vehicles" panel. Holds the
 * paged blocked-VIN rows (with true rule attribution) read from the dedicated
 * `blocked-decisions` artifact.
 */
export type BlockedDecisionsSlice = {
  pages: BlockedDecisionsPage[]
  status: BrowserStatus
  cursor: string | undefined
  /** True once the backend reports a graceful empty page (no artifact for this run). */
  unavailable: boolean
}

function buildEmptyBlockedDecisions(): BlockedDecisionsSlice {
  return { pages: [], status: 'idle', cursor: undefined, unavailable: false }
}

const VIN_MIN_LENGTH = 1
const VIN_MAX_LENGTH = 64
const BROWSER_PAGE_SIZE = 50

/**
 * Pipeline ordering for decision-level stages used by both the browser store
 * and the tab UI. Upstream causes appear before downstream effects.
 *
 * Listing impact remains visible in summaries/counts, but appears last when
 * earlier stage decisions are available.
 */
const BROWSER_STAGES = [
  'preProcessor',
  'feedFieldRules',
  'fieldMapping',
  'groups',
  'listingRules',
] as const satisfies ReadonlyArray<PublishFlowSupportedDecisionStage>

function buildEmptyBrowser(): Record<PublishFlowSupportedDecisionStage, BrowserSlice> {
  // A fresh slice for every stage. The intent is that the manifest
  // tells us which stages are *available* (gated by
  // `availableBrowserStages`), but the slice itself always exists so
  // template code can read `browser.value[stage].pages` without a
  // runtime existence check.
  return {
    preProcessor: { pages: [], status: 'idle', cursor: undefined },
    feedFieldRules: { pages: [], status: 'idle', cursor: undefined },
    fieldMapping: { pages: [], status: 'idle', cursor: undefined },
    groups: { pages: [], status: 'idle', cursor: undefined },
    listingRules: { pages: [], status: 'idle', cursor: undefined },
  }
}

export const useDetailedPublishImpactStore = defineStore('detailedPublishImpact', () => {
  const dialogOpen = ref(false)

  /**
   * Dialog content mode.
   *
   * - `'default'`     — the multi-stage / stage-scoped decision browser.
   * - `'passThrough'` — the Feed Preparation pass-through view for
   *   direct-account / no-preProcessor runs. The pass-through panel owns its
   *   own paged browse + server-side VIN search, so the stage browser and the
   *   cross-stage VIN lookup are suppressed while it is active. This is NOT a
   *   new visible stage — the dialog title stays "Feed Preparation".
   *
   * The non-default mode is a "panel mode": it suppresses the stage browser
   * and the cross-stage VIN lookup.
   */
  const dialogMode = ref<DetailedPublishImpactDialogMode>('default')

  /**
   * Manifest snapshot stamped by `openDialog(manifest)`. The dialog
   * uses `manifest.generatedAt` to render its "Based on this completed
   * publish run (updated <time>)." subtitle. We hold the manifest
   * locally rather than re-reading it so the parent view stays in
   * charge of when artifacts are fetched.
   */
  const manifest = ref<PublishFlowObservabilityManifest | null>(null)

  const vinTrail = ref<PublishVinTrail | null>(null)
  const vinTrailStatus = ref<VinTrailStatus>('idle')
  /** Caller-supplied VIN, trimmed. Used by `lookupVin` to issue the request. */
  const vinTrailQuery = ref('')
  /**
   * Echo of the VIN the dialog should render in headings/results. We
   * snapshot this BEFORE the request so the dialog never flashes a
   * stale VIN if the user submits a new lookup mid-render.
   */
  const vinTrailQueryEcho = ref('')

  const vinTrailReady = computed(() => vinTrailStatus.value === 'ok' && vinTrail.value !== null)

  // -------------------------------------------------------------------------
  // Row-action plumbing.
  //
  // `pendingLookupVin` carries the VIN to feed into the dialog's input;
  // `pendingLookupNonce` is bumped on every `openForVin` so the dialog watcher
  // fires even when the same VIN is clicked repeatedly.
  // -------------------------------------------------------------------------

  const pendingLookupVin = ref('')
  const pendingLookupNonce = ref(0)

  // -------------------------------------------------------------------------
  // Stage-keyed paginated decision browser.
  //
  // Each stage owns an independent pages list, status, and cursor. Switching
  // tabs only changes `activeBrowserStage`; it never clears another stage's
  // loaded pages. VIN lookup and stage browsing also remain independent.
  // -------------------------------------------------------------------------

  const browser = ref<Record<PublishFlowSupportedDecisionStage, BrowserSlice>>(
    buildEmptyBrowser(),
  )

  /**
   * Phase 3 — blocked-decisions drill-down for the listingRules section. Run-wide
   * (one slice), cleared together with the browser slices on run change.
   */
  const blockedDecisions = ref<BlockedDecisionsSlice>(buildEmptyBlockedDecisions())

  /** Flattened blocked-decision rows across all loaded pages. */
  const blockedDecisionRows = computed<BlockedDecisionRecord[]>(() => {
    const out: BlockedDecisionRecord[] = []
    for (const page of blockedDecisions.value.pages) {
      if (Array.isArray(page.records)) out.push(...page.records)
    }
    return out
  })

  /** Total blocked rows reported by the server (full set, not just loaded). */
  const blockedDecisionsTotal = computed<number>(() => {
    const first = blockedDecisions.value.pages[0]
    return typeof first?.totalRecords === 'number' ? first.totalRecords : blockedDecisionRows.value.length
  })

  const blockedDecisionsHasMore = computed(
    () => typeof blockedDecisions.value.cursor === 'string',
  )

  /**
   * Currently selected stage tab. Initialized to `null` so
   * `resolveDefaultBrowserStage` can compute the first available stage from the
   * manifest. Once set, the value persists across dialog close/reopen within
   * the same session. Only `reset()` clears it.
   */
  const activeBrowserStage = ref<PublishFlowSupportedDecisionStage | null>(null)

  /**
   * Optional one-stage scope for stage-card "View details" clicks.
   *
   * When set, the dialog behaves like a stage-specific detail view:
   * - only this stage appears in the browser
   * - tabs are hidden
   * - VIN lookup is hidden because VIN trail is cross-stage
   *
   * Ordinary opens keep this null and preserve the full multi-stage browser.
   */
  const browserStageScope = ref<PublishFlowSupportedDecisionStage | null>(null)

  /**
   * Identity of the publish run whose browser slices are currently cached.
   * When the manifest's `(jazelId, runId)` changes, all stage slices are
   * cleared so a navigation between correlation IDs cannot reuse stale rows
   * or skip a fresh stage-decisions fetch.
   */
  const browserRunKey = ref<string | null>(null)

  /**
   * Stages with at least one decision pointer in the current manifest. Drives
   * tab visibility and default-tab selection.
   *
   * Defensive: tolerates a missing `stageDecisions` and non-array pointer
   * collections.
   */
  const availableBrowserStages = computed<PublishFlowSupportedDecisionStage[]>(() => {
    const decisions = manifest.value?.stageDecisions ?? {}
    const out: PublishFlowSupportedDecisionStage[] = []

    for (const stage of BROWSER_STAGES) {
      const pointers = decisions[stage]
      if (Array.isArray(pointers) && pointers.length > 0) out.push(stage)
    }

    const scoped = browserStageScope.value
    if (!scoped) return out

    return out.includes(scoped) ? [scoped] : []
  })

  const hasListingRulesDecisions = computed(() =>
    availableBrowserStages.value.includes('listingRules'),
  )

  /** Pointer-only gate for the groups decision-level stage. */
  const hasGroupsDecisions = computed(() =>
    availableBrowserStages.value.includes('groups'),
  )

  /** Pointer-only gate for the fieldMapping decision-level stage. */
  const hasFieldMappingDecisions = computed(() =>
    availableBrowserStages.value.includes('fieldMapping'),
  )

  /** Pointer-only gate for the preProcessor decision-level stage. */
  const hasPreProcessorDecisions = computed(() =>
    availableBrowserStages.value.includes('preProcessor'),
  )

  /**
   * Pointer-only gate for Feed-Field Rules decisions. Feed-Field Rules are
   * captured during Field Mapping but surfaced separately for observability.
   */
  const hasFeedFieldRulesDecisions = computed(() =>
    availableBrowserStages.value.includes('feedFieldRules'),
  )

  /**
   * Default-tab pipeline cascade.
   *
   * 1. Reuse `activeBrowserStage` when it is still available.
   * 2. Otherwise choose the earliest available stage in true publish-flow order:
   *    preProcessor -> feedFieldRules
   *    -> fieldMapping -> groups -> listingRules.
   * 3. Return `null` when the manifest has no decision pointers.
   */
  function resolveDefaultBrowserStage(): PublishFlowSupportedDecisionStage | null {
    const available = availableBrowserStages.value
    const current = activeBrowserStage.value
    if (current && available.includes(current)) return current
    for (const stage of BROWSER_STAGES) {
      if (available.includes(stage)) return stage
    }
    return null
  }

  /**
   * Convenience accessor for the dialog's tab pane. Returns `null`
   * when no tab is active (e.g., manifest has no decision pointers).
   * Templates should fall through to the unavailable/empty state in
   * that case.
   */
  const activeBrowserSlice = computed<BrowserSlice | null>(() => {
    const stage = activeBrowserStage.value
    if (!stage) return null
    return browser.value[stage]
  })

  /**
   * Flattened decision records for the active tab. Returns `[]` when
   * no tab is active (defensive: returns "no rows" when nothing has been loaded).
   */
  const activeBrowserRows = computed<PublishDecisionRecord[]>(() => {
    const slice = activeBrowserSlice.value
    if (!slice) return []
    const out: PublishDecisionRecord[] = []
    for (const page of slice.pages) {
      if (Array.isArray(page.records)) out.push(...page.records)
    }
    return out
  })

  const activeBrowserHasMore = computed(
    () => typeof activeBrowserSlice.value?.cursor === 'string',
  )

  /**
   * True if any loaded page in the active tab reports parse warnings
   * or an oversized record. Drives the small "Some results may not
   * be shown." footer (Adjustment #1) so the dealer is aware when
   * the table reflects a partial view of the underlying data.
   */
  const activeBrowserHasIncompleteData = computed(() => {
    const slice = activeBrowserSlice.value
    if (!slice) return false
    return slice.pages.some(
      (page) => Boolean(page.parseWarnings) || Boolean(page.oversizedRecord),
    )
  })

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  /**
   * Structured warning for failed artifact probes.
   *
   * Stage is required for stage-decision calls. VIN-trail failures pass
   * `stage: null` because the API reads across stages. VIN is never logged.
   */
  function emitProbeFailed(payload: {
    probe: 'vin-trail' | 'stage-decisions'
    stage: PublishFlowSupportedDecisionStage | null
    correlationId: string
    reason: string
    message: string
  }): void {
    console.warn(
      JSON.stringify({
        event: 'publishFlow.detailedImpact.probeFailed',
        probe: payload.probe,
        stage: payload.stage,
        correlationId: payload.correlationId,
        reason: payload.reason,
        message: payload.message,
      }),
    )
  }

  function clearAllBrowserState() {
    browser.value = buildEmptyBrowser()
    blockedDecisions.value = buildEmptyBlockedDecisions()
  }

  function manifestRunKey(manifestPayload: PublishFlowObservabilityManifest): string {
    return `${manifestPayload.jazelId}:${manifestPayload.runId}`
  }

  function resolveStageToActivate(
    scopedStage: PublishFlowSupportedDecisionStage | null,
  ): PublishFlowSupportedDecisionStage | null {
    if (scopedStage) return scopedStage

    const current = activeBrowserStage.value
    if (current && availableBrowserStages.value.includes(current)) return current

    return resolveDefaultBrowserStage()
  }

  // -------------------------------------------------------------------------
  // Dialog open/close
  // -------------------------------------------------------------------------

  /**
   * Clears all stage-detail modal state. Scoped and generic publish-impact
   * dialogs share one overlay — always reset before opening a new view.
   */
  function closeAllStageDetails() {
    closeDialog()
  }

  function openDialog(
    manifestPayload: PublishFlowObservabilityManifest,
    options?: {
      stage?: PublishFlowSupportedDecisionStage | null
      mode?: DetailedPublishImpactDialogMode
    },
  ) {
    if (dialogOpen.value) {
      dialogOpen.value = false
      pendingLookupVin.value = ''
    }

    const nextRunKey = manifestRunKey(manifestPayload)
    if (browserRunKey.value !== nextRunKey) {
      clearAllBrowserState()
      browserRunKey.value = nextRunKey
    }

    manifest.value = manifestPayload

    const mode = options?.mode ?? 'default'
    dialogMode.value = mode

    // Panel modes (pass-through) are self-contained views owned by their own
    // panel. The stage browser and cross-stage VIN lookup are intentionally
    // inert here.
    if (PANEL_DIALOG_MODES.has(mode)) {
      browserStageScope.value = null
      activeBrowserStage.value = null
      dialogOpen.value = true
      return
    }

    const scopedStage = options?.stage ?? null
    browserStageScope.value = scopedStage

    const stageToActivate = resolveStageToActivate(scopedStage)
    if (stageToActivate) {
      // Always refetch the stage being opened. Scoped stage-card opens and
      // dialog reopens must not reuse cached slices from an earlier view.
      resetBrowser(stageToActivate)
      activeBrowserStage.value = stageToActivate
    } else {
      activeBrowserStage.value = null
    }

    dialogOpen.value = true
  }

  /**
   * Open the dialog and stage `vin` as a row-action lookup. The dialog
   * watches the bumped `pendingLookupNonce` and consumes
   * `pendingLookupVin` exactly once. The duplicate-network guard still
   * lives in `lookupVin`.
   *
   * VIN is `.trim()`-only here — uppercasing happens server-side.
   */
  function openForVin(vin: string) {
    const trimmed = String(vin || '').trim()
    pendingLookupVin.value = trimmed
    pendingLookupNonce.value = pendingLookupNonce.value + 1
    dialogOpen.value = true
  }

  /**
   * Returns + clears the pending row-action VIN. Returns an empty
   * string when no row action is pending (the dialog watcher uses
   * this to skip the prefill path for ordinary opens). The nonce is
   * deliberately preserved so a repeated `openForVin` for the same
   * VIN still fires the watcher.
   */
  function consumePendingLookup(): string {
    const vin = pendingLookupVin.value
    pendingLookupVin.value = ''
    return vin
  }

  function closeDialog() {
    dialogOpen.value = false
    dialogMode.value = 'default'
    browserStageScope.value = null
    // Drop any pending row-action VIN so the next manual open doesn't
    // accidentally inherit a stale handoff. Nonce is preserved on
    // purpose (see `openForVin`). `activeBrowserStage` is preserved
    // so reopening the dialog lands on the same tab (Guardrail #1).
    pendingLookupVin.value = ''
  }

  /**
   * Set the active stage tab. Pure selector mutation: never clears another
   * stage's slice, the VIN trail, or the manifest.
   */
  function setActiveBrowserStage(stage: PublishFlowSupportedDecisionStage) {
    activeBrowserStage.value = stage
  }

  /**
   * Look up a single VIN's decision trail for the current publish run.
   *
   * Validation + concurrency rules:
   *   - VIN is `.trim()`-only on this side. The backend handles
   *     case-insensitive matching internally; uppercasing here would
   *     desync the echo from what the dealer typed.
   *   - In-flight guard: a second submit while the first is still
   *     loading is a no-op (matches the Preview store's behavior and
   *     avoids racing a stale response into `vinTrailStatus`).
   *
   * Coexistence: only `vinTrail*` state is mutated here. The browser
   * slices are left alone so a VIN lookup never wipes a populated
   * table, on either tab.
   *
   * Mapping rules — all "available outcome" paths are explicit so a
   * future contract change can't silently collapse them into the
   * wrong UI bucket:
   *   - service `ok` + ANY stage `available` with records.length > 0
   *       → `'ok'`
   *   - service `ok` + EVERY stage `available` with records.length === 0
   *     OR NOT `available` (incl. pointer absent)
   *       → `'notInRun'`  (the VIN was not represented anywhere in
   *                         this run)
   *   - service `notFound`     → `'notFound'`     (manifest vanished)
   *   - service `unavailable`  → `'unavailable'`  (transient I/O)
   *
   * A VIN trail is considered available when ANY stage has records. This avoids
   * incorrectly treating fieldMapping-only, feedFieldRules-only, or future
   * stage-specific VIN changes as "not in run".
   */
  async function lookupVin(params: {
    endpoint: string
    jazelId: string
    correlationId: string
    sessionToken: string
    vin: string
  }): Promise<void> {
    if (vinTrailStatus.value === 'loading') return

    const trimmed = String(params.vin || '').trim()
    if (trimmed.length < VIN_MIN_LENGTH || trimmed.length > VIN_MAX_LENGTH) {
      vinTrail.value = null
      vinTrailStatus.value = 'invalid'
      vinTrailQueryEcho.value = trimmed
      return
    }

    vinTrailQuery.value = trimmed
    vinTrailQueryEcho.value = trimmed
    vinTrailStatus.value = 'loading'

    const result = await tryGetPublishFlowVinTrail({
      endpoint: params.endpoint,
      jazelId: params.jazelId,
      correlationId: params.correlationId,
      sessionToken: params.sessionToken,
      vin: trimmed,
    })

    if (result.status === 'ok') {
      // A VIN is represented in the run when any stage has at least one record.
      const trailStages = result.data.stages ?? {}
      const anyAvailableRecords = Object.values(trailStages).some(
        (entry) =>
          entry?.availability === 'available'
          && Array.isArray(entry?.records)
          && entry.records.length > 0,
      )
      vinTrail.value = result.data
      vinTrailStatus.value = anyAvailableRecords ? 'ok' : 'notInRun'
      return
    }
    if (result.status === 'notFound') {
      vinTrail.value = null
      vinTrailStatus.value = 'notFound'
      emitProbeFailed({
        probe: 'vin-trail',
        // Multi-stage call → no single stage. Guardrail #7 bans
        // hardcoded literals here; `null` keeps the field present so
        // dashboards can still group on `event` without a missing
        // key.
        stage: null,
        correlationId: params.correlationId,
        reason: 'notFound',
        message: result.message,
      })
      return
    }
    vinTrail.value = null
    vinTrailStatus.value = 'unavailable'
    emitProbeFailed({
      probe: 'vin-trail',
      stage: null,
      correlationId: params.correlationId,
      reason: result.reason,
      message: result.message,
    })
  }

  // -------------------------------------------------------------------------
  // Stage-keyed browser actions.
  // -------------------------------------------------------------------------

  /**
   * Load the first page for a stage.
   *
   * Strict no-op when:
   * - the stage already has pages, or
   * - the stage is not idle.
   *
   * This is the single source of truth for duplicate first-page fetch
   * prevention across rapid tab switches, watchers, and scripted callers.
   */
  async function loadFirstPage(
    stage: PublishFlowSupportedDecisionStage,
    params: {
      endpoint: string
      jazelId: string
      correlationId: string
      sessionToken: string
    },
  ): Promise<void> {
    const slice = browser.value[stage]
    if (slice.pages.length > 0 || slice.status !== 'idle') return

    slice.status = 'loading'

    try {
      const result = await tryGetPublishFlowStageDecisions({
        endpoint: params.endpoint,
        jazelId: params.jazelId,
        correlationId: params.correlationId,
        stage,
        sessionToken: params.sessionToken,
        pageSize: BROWSER_PAGE_SIZE,
        // Default `groups` browse shows ONLY changed records; default
        // `listingRules` (Website Visibility) browse shows ONLY vehicles that
        // matched blocking rules. Both filters run server-side BEFORE
        // pagination so matching VINs on later pages are not hidden. Search is
        // handled separately (in the dialog) and never sends these flags, so it
        // always spans all evaluated records.
        ...(stage === 'groups' ? { changedOnly: true } : {}),
        ...(stage === 'listingRules' ? { matchedBlockingOnly: true } : {}),
      })

      if (result.status === 'ok') {
        slice.pages = [result.data]
        slice.cursor = result.data.nextCursor
        slice.status = 'ok'
        return
      }
      // Both `notFound` and `unavailable` collapse to `'unavailable'` for
      // the dealer; on-call still gets the distinguishing reason in the
      // structured warn below.
      slice.status = 'unavailable'
      emitProbeFailed({
        probe: 'stage-decisions',
        stage,
        correlationId: params.correlationId,
        reason: result.status === 'notFound' ? 'notFound' : result.reason,
        message: result.message,
      })
    } catch (error) {
      slice.status = 'unavailable'
      emitProbeFailed({
        probe: 'stage-decisions',
        stage,
        correlationId: params.correlationId,
        reason: 'network',
        message: error instanceof Error ? error.message : 'Stage decisions request failed',
      })
    }
  }

  /**
   * Append the next page using the slice's current cursor. No-op when
   * there is no cursor or a request is already in flight, so rapid
   * clicks on the "Load more" button cannot stack duplicates.
   */
  async function loadNextPage(
    stage: PublishFlowSupportedDecisionStage,
    params: {
      endpoint: string
      jazelId: string
      correlationId: string
      sessionToken: string
    },
  ): Promise<void> {
    const slice = browser.value[stage]
    if (slice.status === 'loading') return
    const cursor = slice.cursor
    if (typeof cursor !== 'string' || cursor.length === 0) return

    slice.status = 'loading'

    const result = await tryGetPublishFlowStageDecisions({
      endpoint: params.endpoint,
      jazelId: params.jazelId,
      correlationId: params.correlationId,
      stage,
      sessionToken: params.sessionToken,
      pageSize: BROWSER_PAGE_SIZE,
      cursor,
      // Load More must thread the SAME browse filter as the first page so the
      // paged set stays the same subset (the cursor describes it).
      ...(stage === 'groups' ? { changedOnly: true } : {}),
      ...(stage === 'listingRules' ? { matchedBlockingOnly: true } : {}),
    })

    if (result.status === 'ok') {
      slice.pages = [...slice.pages, result.data]
      slice.cursor = result.data.nextCursor
      slice.status = 'ok'
      return
    }
    // On failure we keep already-loaded pages but flip back to
    // `'unavailable'` so the dialog can surface a banner. Cursor is
    // preserved so the user can retry.
    slice.status = 'unavailable'
    emitProbeFailed({
      probe: 'stage-decisions',
      stage,
      correlationId: params.correlationId,
      reason: result.status === 'notFound' ? 'notFound' : result.reason,
      message: result.message,
    })
  }

  /**
   * Explicit per-stage reset. Required before retrying a failed or already-loaded
   * first page. Touches only the requested slice.
   */
  function resetBrowser(stage: PublishFlowSupportedDecisionStage) {
    browser.value[stage] = { pages: [], status: 'idle', cursor: undefined }
  }

  // -------------------------------------------------------------------------
  // Phase 3 — blocked-decisions drill-down actions.
  // -------------------------------------------------------------------------

  /**
   * Load the first page of blocked-decision rows. Strict no-op when already
   * loaded or in-flight, mirroring `loadFirstPage`. A graceful empty 200
   * (no artifact for this run) maps to `unavailable: true` with `status: 'ok'`
   * so the panel can render "no blocked vehicles" without an error state.
   */
  async function loadBlockedDecisionsFirstPage(params: {
    endpoint: string
    jazelId: string
    correlationId: string
    sessionToken: string
  }): Promise<void> {
    const slice = blockedDecisions.value
    if (slice.pages.length > 0 || slice.status !== 'idle') return

    slice.status = 'loading'
    const result = await tryGetBlockedDecisions({
      endpoint: params.endpoint,
      jazelId: params.jazelId,
      correlationId: params.correlationId,
      sessionToken: params.sessionToken,
    })

    if (result.status === 'ok') {
      slice.pages = [result.data]
      slice.cursor = result.data.nextCursor
      slice.unavailable = result.data.availability === 'unavailable'
      slice.status = 'ok'
      return
    }
    slice.status = 'unavailable'
    emitProbeFailed({
      probe: 'stage-decisions',
      stage: 'listingRules',
      correlationId: params.correlationId,
      reason: result.reason,
      message: result.message,
    })
  }

  /** Append the next blocked-decisions page. No-op without a cursor / while loading. */
  async function loadBlockedDecisionsNextPage(params: {
    endpoint: string
    jazelId: string
    correlationId: string
    sessionToken: string
  }): Promise<void> {
    const slice = blockedDecisions.value
    if (slice.status === 'loading') return
    const cursor = slice.cursor
    if (typeof cursor !== 'string' || cursor.length === 0) return

    slice.status = 'loading'
    const result = await tryGetBlockedDecisions({
      endpoint: params.endpoint,
      jazelId: params.jazelId,
      correlationId: params.correlationId,
      sessionToken: params.sessionToken,
      cursor,
    })

    if (result.status === 'ok') {
      slice.pages = [...slice.pages, result.data]
      slice.cursor = result.data.nextCursor
      slice.status = 'ok'
      return
    }
    slice.status = 'unavailable'
    emitProbeFailed({
      probe: 'stage-decisions',
      stage: 'listingRules',
      correlationId: params.correlationId,
      reason: result.reason,
      message: result.message,
    })
  }

  function resetBlockedDecisions() {
    blockedDecisions.value = buildEmptyBlockedDecisions()
  }

  /**
   * Reset transient state without closing the dialog. The manifest survives so
   * the subtitle stays accurate. The active browser stage is preserved for the
   * current session.
   */
  function resetTransientState() {
    vinTrail.value = null
    vinTrailStatus.value = 'idle'
    vinTrailQuery.value = ''
    vinTrailQueryEcho.value = ''
    clearAllBrowserState()
  }

  /**
   * Hard reset (account switch / logout). Drops the manifest, the
   * dialog flag, the active tab selection, AND the row-action nonce
   * so a subsequent session starts from a fully clean slate.
   */
  function reset() {
    resetTransientState()
    manifest.value = null
    dialogOpen.value = false
    dialogMode.value = 'default'
    activeBrowserStage.value = null
    browserStageScope.value = null
    browserRunKey.value = null
    pendingLookupVin.value = ''
    pendingLookupNonce.value = 0
  }

  return {
    dialogOpen,
    dialogMode,
    manifest,

    vinTrail,
    vinTrailStatus,
    vinTrailQuery,
    vinTrailQueryEcho,
    vinTrailReady,

    pendingLookupVin,
    pendingLookupNonce,

    browser,
    blockedDecisions,
    blockedDecisionRows,
    blockedDecisionsTotal,
    blockedDecisionsHasMore,
    activeBrowserStage,
    browserStageScope,
    activeBrowserSlice,
    activeBrowserRows,
    activeBrowserHasMore,
    activeBrowserHasIncompleteData,
    availableBrowserStages,
    hasListingRulesDecisions,
    hasGroupsDecisions,
    hasFieldMappingDecisions,
    hasPreProcessorDecisions,
    hasFeedFieldRulesDecisions,
    resolveDefaultBrowserStage,

    openDialog,
    openForVin,
    consumePendingLookup,
    closeAllStageDetails,
    closeDialog,
    setActiveBrowserStage,
    lookupVin,
    loadFirstPage,
    loadNextPage,
    resetBrowser,
    loadBlockedDecisionsFirstPage,
    loadBlockedDecisionsNextPage,
    resetBlockedDecisions,
    resetTransientState,
    reset,
  }
})

/**
 * Re-export for tests that need to construct fixtures without
 * importing from the service module directly.
 */
export type {
  PublishVinTrail,
  PublishStageDecisionsPage,
  PublishFlowObservabilityManifest,
  PublishFlowSupportedDecisionStage,
}

/**
 * Validation thresholds exported for the dialog's input maxlength
 * attribute and tests. Keeping these in one place avoids drift between
 * the store's runtime check and the template's user-facing limit.
 */
export const VIN_VALIDATION = {
  minLength: VIN_MIN_LENGTH,
  maxLength: VIN_MAX_LENGTH,
} as const

/** Page size constant exported so tests and the dialog stay in sync. */
export const BROWSER_PAGE_SIZE_DEFAULT = BROWSER_PAGE_SIZE

/** Publish pipeline order for the tab strip (and tests). */
export const BROWSER_STAGE_ORDER = BROWSER_STAGES
